import React, { useState } from 'react';
import styled from 'styled-components';
import { submitQuizAnswers } from '../services/quizService';

const QuizContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: var(--tg-theme-separator-color, #e5e5e5);
  border-radius: 4px;
  margin-bottom: 24px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: var(--tg-theme-button-color, #2481cc);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const QuestionCard = styled.div`
  background-color: var(--tg-theme-secondary-bg-color, #f8f9fa);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid var(--tg-theme-separator-color, #e5e5e5);
`;

const QuestionNumber = styled.div`
  color: var(--tg-theme-hint-color, #999999);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
`;

const QuestionText = styled.h2`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--tg-theme-text-color, #000000);
`;

const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AnswerOption = styled.label`
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: var(--tg-theme-bg-color, #ffffff);
  border: 2px solid ${props => props.selected 
    ? 'var(--tg-theme-button-color, #2481cc)' 
    : 'var(--tg-theme-separator-color, #e5e5e5)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--tg-theme-button-color, #2481cc);
    background-color: ${props => props.selected 
      ? 'var(--tg-theme-button-color, #2481cc)' 
      : 'var(--tg-theme-secondary-bg-color, #f8f9fa)'};
  }
  
  ${props => props.selected && `
    background-color: var(--tg-theme-button-color, #2481cc);
    color: var(--tg-theme-button-text-color, #ffffff);
  `}
`;

const RadioInput = styled.input`
  margin-right: 12px;
  width: 20px;
  height: 20px;
  accent-color: var(--tg-theme-button-color, #2481cc);
`;

const AnswerText = styled.span`
  font-size: 16px;
  line-height: 1.4;
  flex: 1;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
`;

const Button = styled.button`
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--tg-theme-button-color, #2481cc);
  color: var(--tg-theme-button-text-color, #ffffff);
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: var(--tg-theme-secondary-bg-color, #f8f9fa);
  color: var(--tg-theme-text-color, #000000);
  border: 1px solid var(--tg-theme-separator-color, #e5e5e5);
  
  &:hover:not(:disabled) {
    background-color: var(--tg-theme-separator-color, #e5e5e5);
  }
`;

const SubmitContainer = styled.div`
  text-align: center;
  padding: 32px 0;
`;

const SummaryText = styled.p`
  margin: 0 0 24px 0;
  font-size: 16px;
  color: var(--tg-theme-text-color, #000000);
  line-height: 1.5;
`;

function QuizComponent({ quiz, userId, onComplete, onError }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;
  const hasAnswer = answers[currentQuestion.question] !== undefined;

  const handleAnswerSelect = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.question]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await submitQuizAnswers(quiz.id, userId, answers);
      onComplete();
    } catch (error) {
      console.error('Ошибка отправки ответов:', error);
      onError(error.message || 'Не удалось отправить ответы');
    } finally {
      setIsSubmitting(false);
    }
  };

  const completedAnswers = Object.keys(answers).length;
  const allQuestionsAnswered = completedAnswers === quiz.questions.length;

  return (
    <QuizContainer>
      <ProgressBar>
        <ProgressFill progress={progress} />
      </ProgressBar>

      <QuestionCard>
        <QuestionNumber>
          Вопрос {currentQuestionIndex + 1} из {quiz.questions.length}
        </QuestionNumber>
        
        <QuestionText>
          {currentQuestion.question}
        </QuestionText>

        <AnswersContainer>
          {currentQuestion.answers.map((answer, index) => (
            <AnswerOption
              key={index}
              selected={answers[currentQuestion.question] === answer}
              onClick={() => handleAnswerSelect(answer)}
            >
              <RadioInput
                type="radio"
                name={`question-${currentQuestionIndex}`}
                value={answer}
                checked={answers[currentQuestion.question] === answer}
                onChange={() => handleAnswerSelect(answer)}
              />
              <AnswerText>{answer}</AnswerText>
            </AnswerOption>
          ))}
        </AnswersContainer>
      </QuestionCard>

      {isLastQuestion && hasAnswer ? (
        <SubmitContainer>
          <SummaryText>
            Вы ответили на все {quiz.questions.length} вопросов.
            <br />
            Нажмите "Завершить", чтобы отправить ответы.
          </SummaryText>
          <PrimaryButton 
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered || isSubmitting}
          >
            {isSubmitting ? 'Отправка...' : 'Завершить опрос'}
          </PrimaryButton>
        </SubmitContainer>
      ) : (
        <NavigationContainer>
          <SecondaryButton
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            Назад
          </SecondaryButton>

          <PrimaryButton
            onClick={handleNext}
            disabled={!hasAnswer}
          >
            {isLastQuestion ? 'К завершению' : 'Далее'}
          </PrimaryButton>
        </NavigationContainer>
      )}
    </QuizContainer>
  );
}

export default QuizComponent; 